/**
 * Shadow DOM widget host — creates the trigger button and manages the modal.
 */

import type { ResolvedConfig } from "../types";
import { createModal, type ModalController } from "./modal";
import { showToast } from "./toast";
import { collectMetadata } from "../capture/metadata";
import { captureScreenshot } from "../capture/screenshot";
import { submitFeedback } from "../api/submit";
import { prefetch, clearCache } from "../api/jpd-cache";
import {
  startShakeDetection,
  stopShakeDetection,
  requestMotionPermission,
  setThreshold,
} from "../detection/shake";
import styles from "./styles.css?inline";

// Feedback icon SVG
const FEEDBACK_ICON = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>`;

let widgetRoot: HTMLDivElement | null = null;
let shadowRoot: ShadowRoot | null = null;
let modal: ModalController | null = null;
let capturing = false;
let motionPermissionRequested = false;

export function createWidget(config: ResolvedConfig): void {
  if (widgetRoot) return; // Already created

  // Load Cabin font
  loadFont();

  // Create host element
  widgetRoot = document.createElement("div");
  widgetRoot.id = "ip-feedback-root";
  shadowRoot = widgetRoot.attachShadow({ mode: "open" });

  // Inject styles
  const styleEl = document.createElement("style");
  styleEl.textContent = styles;
  shadowRoot.appendChild(styleEl);

  // Create trigger button
  const trigger = document.createElement("button");
  trigger.className = `ip-trigger ${config.position}`;
  trigger.innerHTML = `${FEEDBACK_ICON}<span>${config.buttonText}</span>`;
  trigger.setAttribute("aria-label", `Open feedback form`);
  trigger.addEventListener("click", () => handleTrigger(config));

  // iOS motion permission: request on first tap
  if (config.shakeEnabled && !motionPermissionRequested) {
    trigger.addEventListener(
      "click",
      async () => {
        motionPermissionRequested = true;
        const granted = await requestMotionPermission();
        if (granted) {
          setThreshold(config.shakeThreshold);
          startShakeDetection(config.shakeThreshold, () => handleTrigger(config));
        }
      },
      { once: true },
    );
  }

  shadowRoot.appendChild(trigger);

  // Create toast container
  const toast = document.createElement("div");
  toast.className = "ip-toast";
  toast.setAttribute("role", "status");
  toast.setAttribute("aria-live", "polite");
  shadowRoot.appendChild(toast);

  // Mount to page
  document.body.appendChild(widgetRoot);

  // Prefetch JPD ideas + segments so they're ready when the modal opens
  if (config.jpdProject && config.apiUrl) {
    prefetch(config.apiUrl, config.jpdProject);
  }

  // Start shake detection (non-iOS — no permission needed)
  if (config.shakeEnabled) {
    setThreshold(config.shakeThreshold);
    startShakeDetection(config.shakeThreshold, () => handleTrigger(config));
  }
}

async function handleTrigger(config: ResolvedConfig): Promise<void> {
  if (modal || capturing) return; // Already open or capturing

  // Pause shake detection while modal is open
  stopShakeDetection();

  // Capture screenshot before showing the modal overlay
  capturing = true;
  const screenshotBlob = await captureScreenshot(widgetRoot);
  capturing = false;

  // Create and show modal
  modal = createModal(shadowRoot!, {
    jpdProject: config.jpdProject,
    apiUrl: config.apiUrl,
    onSubmit: async ({
      summary,
      text,
      feedbackType,
      contactEmail,
      jpdIdeaKey,
      jpdIdeaSummary,
      customerSegment,
      attachmentBlob,
      attachmentContentType,
    }) => {
      modal?.setSubmitting(true);

      try {
        const metadata = collectMetadata();
        const result = await submitFeedback({
          config,
          summary,
          text,
          feedbackType,
          contactEmail,
          jpdIdeaKey,
          jpdIdeaSummary,
          customerSegment,
          screenshotBlob,
          attachmentBlob,
          attachmentContentType,
          metadata,
        });

        modal?.close();
        modal = null;

        showToast(shadowRoot!, "success", `Submitted: ${result.issueKey}`);
        config.onSubmitSuccess(result.issueKey);
      } catch (err) {
        modal?.setSubmitting(false);
        const error = err instanceof Error ? err : new Error(String(err));
        showToast(shadowRoot!, "error", "Failed to submit. Please try again.");
        config.onSubmitError(error);
      }
    },
    onCancel: () => {
      modal?.close();
      modal = null;

      // Resume shake detection
      if (config.shakeEnabled) {
        startShakeDetection(config.shakeThreshold, () => handleTrigger(config));
      }
    },
  });

  // Resume shake detection on close
  const originalClose = modal.close;
  modal.close = () => {
    originalClose();
    if (config.shakeEnabled) {
      startShakeDetection(config.shakeThreshold, () => handleTrigger(config));
    }
  };
}

export function openWidget(config: ResolvedConfig): void {
  handleTrigger(config);
}

export function destroyWidget(): void {
  clearCache();
  stopShakeDetection();
  if (modal) {
    modal.close();
    modal = null;
  }
  if (widgetRoot) {
    widgetRoot.remove();
    widgetRoot = null;
    shadowRoot = null;
  }
}

function loadFont(): void {
  if (document.querySelector("link[data-ip-feedback-font]")) return;
  const link = document.createElement("link");
  link.setAttribute("data-ip-feedback-font", "");
  link.rel = "stylesheet";
  link.href = "https://fonts.googleapis.com/css2?family=Cabin:wght@400;600;700&display=swap";
  document.head.appendChild(link);
}
